<template>
  <div class="settings-changelog-filters-bar">
    <div class="relative settings-changelog-filters-bar__search">
      <i class="pi pi-search settings-changelog-filters-bar__search-icon" aria-hidden="true" />
      <InputText
        :model-value="search"
        placeholder="Search field name..."
        class="w-full settings-changelog-filters-bar__search-input"
        @update:model-value="onSearchInput"
        @keyup.enter="emitApply"
      />
    </div>

    <Select
      :model-value="action"
      :options="CHANGELOG_ACTION_OPTIONS"
      optionLabel="label"
      optionValue="value"
      placeholder="All actions"
      showClear
      class="settings-changelog-filters-bar__select"
      @update:model-value="onActionChange"
    />

    <Select
      :model-value="entityType"
      :options="entityTypeOptions"
      optionLabel="label"
      optionValue="value"
      placeholder="All sources"
      showClear
      class="settings-changelog-filters-bar__select settings-changelog-filters-bar__select--entity"
      @update:model-value="onEntityTypeChange"
    />

    <DatePicker
      :model-value="dateFromModel"
      :date-format="PRIME_DATE_FORMAT_US"
      :placeholder="DISPLAY_DATE_PLACEHOLDER_US"
      show-icon
      :manual-input="false"
      class="settings-changelog-filters-bar__date"
      @update:model-value="onDateFromChange"
    />

    <DatePicker
      :model-value="dateToModel"
      :date-format="PRIME_DATE_FORMAT_US"
      :placeholder="DISPLAY_DATE_PLACEHOLDER_US"
      show-icon
      :manual-input="false"
      class="settings-changelog-filters-bar__date"
      @update:model-value="onDateToChange"
    />

    <Button
      label="Clear"
      size="small"
      severity="secondary"
      outlined
      :disabled="!hasFilters"
      @click="$emit('clear')"
    />
  </div>
</template>

<script setup>
import { computed } from 'vue';
import Button from 'primevue/button';
import DatePicker from 'primevue/datepicker';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import { isValidDateRange, toApiDate } from '@/constants/attendance';
import { PRIME_DATE_FORMAT_US, DISPLAY_DATE_PLACEHOLDER_US } from '@/constants/dateFormats';
import { parseDateSafe } from '@/utils/dateHelper';
import {
  CHANGELOG_ACTION_OPTIONS,
  CHANGELOG_ENTITY_TYPE_OPTIONS,
  hasActiveChangelogFilters,
} from '../constants/changelogFilters';

const props = defineProps({
  search: { type: String, default: '' },
  action: { type: String, default: null },
  entityType: { type: String, default: null },
  dateFrom: { type: String, default: '' },
  dateTo: { type: String, default: '' },
  canViewCompensation: { type: Boolean, default: false },
});

const emit = defineEmits([
  'update:search',
  'update:action',
  'update:entityType',
  'update:dateFrom',
  'update:dateTo',
  'apply',
  'clear',
]);

const entityTypeOptions = computed(() => {
  if (props.canViewCompensation) return CHANGELOG_ENTITY_TYPE_OPTIONS;
  return CHANGELOG_ENTITY_TYPE_OPTIONS.filter((opt) => opt.value !== 'employee_compensation');
});

const dateFromModel = computed(() => parseDateSafe(props.dateFrom));
const dateToModel = computed(() => parseDateSafe(props.dateTo));

const hasFilters = computed(() =>
  hasActiveChangelogFilters({
    search: props.search,
    action: props.action,
    entity_type: props.entityType,
    date_from: props.dateFrom,
    date_to: props.dateTo,
  })
);

let searchTimer = null;

function emitApply() {
  emit('apply');
}

function onSearchInput(value) {
  emit('update:search', value ?? '');
  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = setTimeout(emitApply, 400);
}

function onActionChange(value) {
  emit('update:action', value ?? null);
  emitApply();
}

function onEntityTypeChange(value) {
  emit('update:entityType', value ?? null);
  emitApply();
}

function onDateFromChange(value) {
  const from = toApiDate(value) || '';
  emit('update:dateFrom', from);
  if (!from || !props.dateTo || isValidDateRange(from, props.dateTo)) {
    emitApply();
  }
}

function onDateToChange(value) {
  const to = toApiDate(value) || '';
  emit('update:dateTo', to);
  if (!to || !props.dateFrom || isValidDateRange(props.dateFrom, to)) {
    emitApply();
  }
}
</script>

<style scoped>
.settings-changelog-filters-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
}

.settings-changelog-filters-bar__search {
  flex: 1 1 12rem;
  min-width: 12rem;
}

.settings-changelog-filters-bar__search-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--crc-text-muted, var(--text-color-secondary));
  font-size: 0.875rem;
  pointer-events: none;
}

.settings-changelog-filters-bar__search-input {
  padding-left: 2.25rem;
  border-radius: 0.75rem;
}

.settings-changelog-filters-bar__select {
  width: 9.5rem;
  min-width: 9rem;
}

.settings-changelog-filters-bar__select--entity {
  width: 10.5rem;
  min-width: 10rem;
}

.settings-changelog-filters-bar__date {
  width: 8.5rem;
  min-width: 8rem;
}
</style>
